import { Duration } from '@boc/duration';
import { genHrefLangs } from '@boc/framework';
import { getAnonClient } from '@boc/framework/client';
import { generateCacheControlV2 } from '@boc/framework/headers/cache';
import {
  type CollectionBasicFragment,
  CollectionsDocument,
  type CollectionsQuery,
  type CollectionsQueryVariables,
  ProductsWithVariantsDocument,
  type ProductsWithVariantsQuery,
  type ProductsWithVariantsQueryVariables,
  type ProductVariantCardFragment,
} from '@boc/framework/saleor-graphql';
import { mapEdgesToItems } from '@boc/framework/saleor-utils';
import { TwMaxWidthPicture, TwPicture } from '@boc/images';
import Autoscroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';
import type { MetaDescriptor } from 'react-router';
import { data, Link } from 'react-router';

import { getCache } from '~/framework.server/cache';
import { buttonVariants } from '~/local/components/button';
import { Price } from '~/local/components/price';
import Hero from '~/local/homepage/components/hero';
import Homepageslides from '~/local/homepage/components/homepageslides';
import { SectionWithHeading } from '~/local/homepage/components/sections';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from '~/local/homepage/components/slider';
import reviews from '~/local/homepage/reviews';
import env from '~/secrets.server';
import { canonicalize, imgUrl, parseParams, urls } from '~/urls';

import type { Route } from './+types/route';
import { ProductCarousel } from './components/carousel';
import ProductTabs from './components/product-tabs';
import { Section } from './components/section';
import { HOMEPAGE_COLLECTIONS, OWN_BRANDS } from '#/brand/collections';
import { channels, DEFAULT_CHANNEL } from '#/channels';
import { DEFAULT_LOCALE, supportedLocales } from '#/locales';

export const meta: Route.MetaFunction = ({ params, location }) => {
  const { channel } = parseParams(params);

  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  const countryName = regionNames.of(channel.defaultCountry);

  const canonical: MetaDescriptor = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalize(location),
  };

  const hreflangs = genHrefLangs({
    location,
    canonicalize,
    url: urls.home,
    channels: Object.values(channels),
    defaultChannel: DEFAULT_CHANNEL,
    defaultLocale: DEFAULT_LOCALE,
    supportedLocales,
  });

  return [
    canonical,
    ...hreflangs,
    {
      title: `Buy the best jigsaw puzzles in ${countryName} only at Jigsaw Nation`,
    },
    {
      name: 'description',
      content:
        'Jigsaw Nation is your premier destination for captivating Indian art-themed jigsaw puzzles. Immerse yourself in a world of vibrant colors and intricate designs. Shop now for unique puzzles, perfect for solo enjoyment or thoughtful personalized gifts. Elevate your puzzle experience with our custom photo puzzles, turning memories into interactive art. Explore the beauty of India, piece by piece.',
    },
  ];
};

export const headers: Route.HeadersFunction = ({ loaderHeaders }) => {
  const loaderCacheHeader = loaderHeaders.get('Cache-Control');

  if (loaderCacheHeader) {
    return { 'Cache-Control': loaderCacheHeader };
  }

  return {
    'Cache-Control': generateCacheControlV2({
      maxAge: Duration.minutes(5),
      sharedMaxAge: Duration.hours(1),
    }),
  };
};

export const loader = async ({ params }: Route.LoaderArgs) => {
  const { channel, saleorLocale } = parseParams(params);

  const ttl = Duration.days(1);

  const cache = getCache();

  const client = getAnonClient({ apiUrl: env.SALEOR_API_URL });

  const promiseCollectionsSpecial = cache
    .getOrSet<CollectionsQuery>(
      `homepage-collections-${channel.slug}-${saleorLocale}`,
      async () => {
        return client.request<CollectionsQuery, CollectionsQueryVariables>(CollectionsDocument, {
          channel: channel.slug,
          locale: saleorLocale,
          filter: {
            slugs: HOMEPAGE_COLLECTIONS,
          },
          first: 100,
        });
      },
      ttl
    )
    .then((response) => response.collections?.edges.map((e) => e.node));

  const [collectionsSpecial] = await Promise.all([promiseCollectionsSpecial]);

  const prodNewArrivals = cache
    .getOrSet<ProductsWithVariantsQuery>(
      `homepage-new-arrivals-${channel.slug}-${saleorLocale}-v2`,
      async () => {
        return client.request<ProductsWithVariantsQuery, ProductsWithVariantsQueryVariables>(
          ProductsWithVariantsDocument,
          {
            channel: channel.slug,
            locale: saleorLocale,
            sortBy: {
              direction: 'DESC',
              field: 'PUBLICATION_DATE',
            },
            first: 12,
          }
        );
      },
      ttl
    )
    .then((response) => {
      const variants: ProductVariantCardFragment[] = [];

      for (const edge of response.products?.edges || []) {
        const productVariants = mapEdgesToItems<ProductVariantCardFragment>(
          edge.node.productVariants
        );

        variants.push(...productVariants);
      }

      return variants;
    });

  const prodOwnProducts = cache
    .getOrSet<ProductsWithVariantsQuery>(
      `homepage-own-products-${channel.slug}-${saleorLocale}-v2`,
      async () => {
        return client.request<ProductsWithVariantsQuery, ProductsWithVariantsQueryVariables>(
          ProductsWithVariantsDocument,
          {
            channel: channel.slug,
            locale: saleorLocale,
            filter: {
              attributes: [{ slug: 'puzzle-publisher', values: OWN_BRANDS }],
            },
            first: 24,
          }
        );
      },
      ttl
    )
    .then((response) => {
      const variants: ProductVariantCardFragment[] = [];

      for (const edge of response.products?.edges || []) {
        const productVariants = mapEdgesToItems<ProductVariantCardFragment>(
          edge.node.productVariants
        );

        variants.push(...productVariants);
      }

      return variants;
    });

  const getProductPricing = (slug: string) =>
    cache
      .getOrSet<ProductsWithVariantsQuery>(
        `homepage-pricing-${slug}-${channel.slug}-${saleorLocale}`,
        async () => {
          return client.request<ProductsWithVariantsQuery, ProductsWithVariantsQueryVariables>(
            ProductsWithVariantsDocument,
            {
              channel: channel.slug,
              locale: saleorLocale,
              filter: {
                slugs: [slug],
              },
              first: 1,
            }
          );
        },
        Duration.hours(1)
      )
      .then((response) => {
        const product = response.products?.edges[0]?.node;

        if (!product) {
          return null;
        }

        const variants = mapEdgesToItems<ProductVariantCardFragment>(product.productVariants);

        return variants[0]?.pricing ?? null;
      });

  const promiseScrumptiousPricing = getProductPricing('scrumptious');
  const promiseKitchenPricing = getProductPricing('the-kitchen');

  const prodCollections = cache
    .getOrSet<ProductsWithVariantsQuery>(
      `homepage-collections-products-${channel.slug}-${saleorLocale}-v2`,
      async () => {
        return client.request<ProductsWithVariantsQuery, ProductsWithVariantsQueryVariables>(
          ProductsWithVariantsDocument,
          {
            channel: channel.slug,
            locale: saleorLocale,
            filter: {
              collections: collectionsSpecial?.map((e) => e.id),
            },
            first: 24,
          }
        );
      },
      ttl
    )
    .then((response) => {
      const variants: ProductVariantCardFragment[] = [];

      for (const edge of response.products?.edges || []) {
        const productVariants = mapEdgesToItems<ProductVariantCardFragment>(
          edge.node.productVariants
        );

        variants.push(...productVariants);
      }

      return variants;
    });

  return data(
    {
      products: {
        newArrivals: await prodNewArrivals,
        collections: await prodCollections,
        own: await prodOwnProducts,
      },
      collections: {
        special: collectionsSpecial,
      },
      scrumptiousPricing: await promiseScrumptiousPricing,
      kitchenPricing: await promiseKitchenPricing,
    },
    {
      headers: {
        'Cache-Control': generateCacheControlV2({
          maxAge: Duration.minutes(5),
          sharedMaxAge: Duration.hours(1),
        }),
      },
    }
  );
};

const variantHasCollection = (
  variant: ProductVariantCardFragment,
  collection: CollectionBasicFragment
) => {
  return variant.product.collections?.some((e) => e.id == collection.id);
};

export default function Page({ loaderData, params }: Route.ComponentProps) {
  const { products, collections, scrumptiousPricing, kitchenPricing } = loaderData;

  const scrumptiousPrice = scrumptiousPricing?.price?.gross;
  const scrumptiousPriceUndiscounted = scrumptiousPricing?.priceUndiscounted?.gross;
  const scrumptiousOnSale =
    scrumptiousPrice &&
    scrumptiousPriceUndiscounted &&
    scrumptiousPriceUndiscounted.amount > scrumptiousPrice.amount;

  const kitchenPrice = kitchenPricing?.price?.gross;
  const kitchenPriceUndiscounted = kitchenPricing?.priceUndiscounted?.gross;
  const kitchenOnSale =
    kitchenPrice && kitchenPriceUndiscounted && kitchenPriceUndiscounted.amount > kitchenPrice.amount;

  const slides = [
    <Homepageslides
      key={5}
      varient={'slide5'}
      slideContent={
        <div className="relative inline-block h-full">
          <TwPicture
            imgUrl={imgUrl}
            src={`/static/homepage/animated_image.png`}
            alt="Scrumptious"
            defaultSize={558}
            twSizes={{ sm: 720, lg: 800 }}
            className="h-full w-full object-cover md:h-full"
          />
          <div className="absolute bottom-4 left-0 px-10 pb-6 md:bottom-0">
            <p className="font-heading-condensed w-fit bg-[#BC3865] px-4 text-6xl font-medium text-[#F4C855] md:text-7xl">
              Scrumptious,
            </p>
            <p className="font-heading-condensed w-fit bg-[#BC3865] px-4 text-3xl font-medium text-[#F4C855] md:text-7xl">
              A 1000 piece challenge
              {scrumptiousPrice && (
                <>
                  {' '}
                  now at just{' '}
                  {scrumptiousOnSale && (
                    <>
                      <Price
                        price={scrumptiousPriceUndiscounted}
                        truncate
                        className="line-through decoration-wavy decoration-yellow-300 opacity-70"
                      />{' '}
                    </>
                  )}
                  <Price price={scrumptiousPrice} truncate />
                </>
              )}
            </p>
            <button
              className="hover:bg-primary-1-40 disabled:bg-neutral-2-20 font-heading-condensed disabled:text-neutral-2-40 bg-[#BC3865] px-6 py-4 text-center text-lg leading-4 font-medium tracking-wide text-[#F4C855] uppercase after:content-['_⟶'] disabled:border-0 disabled:opacity-50 md:text-5xl"
              type="button"
            >
              <Link
                to={urls.products.details(params, {
                  slug: 'scrumptious',
                })}
              >
                Buy now
              </Link>
            </button>
          </div>
        </div>
      }
    />,

    // <Homepageslides
    //   key={2}
    //   varient={'slide1'}
    //   slideContent={
    //     <>
    //       <div className="flex flex-col gap-5">
    //         <p className="font-heading-condensed text-primary-2-80 text-5xl leading-10 font-medium tracking-tight sm:text-8xl sm:leading-20">
    //           Not just a matchbox; <br /> even better.
    //         </p>
    //         <p className="text-neutral-2 font-family-content font-bold">
    //           At Jigsaw Nation we love puzzles & matchboxes so we created matchbox puzzles.
    //         </p>
    //         <Link
    //           className={buttonVariants({
    //             intent: 'primary',
    //             className: 'font-family-heading w-max font-bold',
    //           })}
    //           to={urls.products.jigsawPuzzles(params, {
    //             series: 'matchbox',
    //           })}
    //         >
    //           BROWSE COLLECTION
    //         </Link>
    //       </div>
    //       <div className="relative -mt-36 h-full w-full md:h-full md:w-full">
    //         <TwPicture
    //           imgUrl={imgUrl}
    //           src={`/static/homepage/hero-matchbox.png`}
    //           alt={`Not an ordinary matchbox`}
    //           defaultSize={558}
    //           twSizes={{ sm: 720, lg: 750 }}
    //           className="h-full w-full object-cover lg:object-contain"
    //         />
    //       </div>
    //     </>
    //   }
    // />,

    <Homepageslides
      key={2}
      varient={'slide8'}
      slideContent={
        <>
          <div className="flex w-full max-w-[520px] flex-col items-center gap-5 px-6 md:w-1/2 md:items-start md:px-10">
            <p className="font-heading-condensed w-fit rounded-xl bg-[#4A2B7E] px-4 text-center text-5xl leading-[1.1] font-medium tracking-tight text-[#F4C855] sm:text-7xl md:text-left">
              The Kitchen, <br /> served in 280 pieces
              {kitchenPrice && (
                <>
                  {' '}
                  at just{' '}
                  {kitchenOnSale && (
                    <>
                      <Price
                        price={kitchenPriceUndiscounted}
                        truncate
                        className="line-through decoration-wavy decoration-[#F27BA7] opacity-70"
                      />{' '}
                    </>
                  )}
                  <Price price={kitchenPrice} truncate />
                </>
              )}
            </p>
            <p className="font-family-content w-fit rounded-xl bg-[#4A2B7E] px-4 py-1 text-center font-bold text-[#F4C855] md:text-left">
              Joyful clutter from the heart of the home, by Muhammed Sajid.
            </p>
            <Link
              to={urls.products.details(params, { slug: 'the-kitchen' })}
              className="disabled:bg-neutral-2-20 disabled:text-neutral-2-40 w-fit rounded-lg bg-[#1D8A3C] px-6 py-4 text-base leading-4 font-medium tracking-wide text-white uppercase after:content-['_⟶'] hover:bg-[#166B2E] disabled:border-0 disabled:opacity-50"
            >
              Puzzle it together
            </Link>
          </div>
          <div className="relative flex w-full justify-center md:w-1/2 md:justify-end md:pr-10">
            <TwPicture
              imgUrl={imgUrl}
              src="/static/homepage/the-kitchen.jpg"
              alt="The Kitchen, a 280 piece puzzle by Muhammed Sajid"
              defaultSize={558}
              twSizes={{ sm: 720, lg: 750 }}
              className="h-60 object-contain drop-shadow-xl md:h-140"
            />
          </div>
        </>
      }
    />,

    // <Homepageslides
    //   key={3}
    //   varient={'slide2'}
    //   slideContent={
    //     <>
    //       <div>
    //         <TwPicture
    //           imgUrl={imgUrl}
    //           src={`/static/homepage/moon.png`}
    //           alt={`Interstellar Moon`}
    //           defaultSize={558}
    //           twSizes={{ sm: 720, lg: 2000 }}
    //           className="h-auto w-full object-cover lg:object-contain"
    //         />
    //       </div>
    //       <div className="px-4 sm:px-6 md:px-0">
    //         <div className="mb-10 flex flex-col items-center gap-10 sm:gap-16 md:flex-row">
    //           <p className="font-heading-condensed text-center text-5xl font-semibold text-[#FFFFFF] sm:text-6xl md:text-left md:text-7xl">
    //             A trip to the moon!
    //           </p>
    //           <div className="flex flex-col justify-start gap-3 text-center md:text-left">
    //             <p className="text-neutral-1">
    //               The interstellar puzzle is ready for <br /> launch, Supermoon24.
    //             </p>
    //             <Link
    //               className="hover:bg-primary-1-40 font-family-content text-primary-2-10 disabled:bg-neutral-2-20 disabled:text-neutral-2-40 w-fit self-center rounded-full bg-[#354043] px-6 py-4 text-base leading-4 font-extrabold tracking-wide uppercase after:content-['_⟶'] disabled:border-0 disabled:opacity-50 md:self-start"
    //               to={urls.products.details(params, {
    //                 slug: 'supermoon24',
    //               })}
    //             >
    //               BUY NOW
    //             </Link>
    //           </div>
    //         </div>
    //       </div>
    //     </>
    //   }
    // />,

    <Homepageslides
      key={4}
      varient={'slide4'}
      slideContent={
        <>
          {/* Desktop Layout */}
          <div className="hidden h-full flex-col justify-between md:flex">
            <TwPicture
              imgUrl={imgUrl}
              src={'/static/homepage/miranda1.png'}
              alt="Crowded place"
              defaultSize={558}
              twSizes={{ sm: 720, lg: 200 }}
              className="w-auto max-w-[250px] object-contain"
            />
            <TwPicture
              imgUrl={imgUrl}
              src={'/static/homepage/miranda2.png'}
              alt="Book Shop"
              defaultSize={558}
              twSizes={{ sm: 720, lg: 200 }}
              className="w-auto max-w-[250px] rounded-t-3xl object-contain"
            />
          </div>

          <div className="hidden h-full items-center justify-center md:flex">
            <TwPicture
              imgUrl={imgUrl}
              src={'/static/homepage/miranda3.png'}
              alt="Mario Miranda"
              defaultSize={558}
              twSizes={{ sm: 720, lg: 200 }}
              className="w-auto max-w-[250px] object-contain"
            />
          </div>

          <div className="hidden h-full flex-col items-start justify-center gap-y-3 md:flex">
            <p className="font-heading-condensed text-6xl leading-11 font-medium tracking-tight text-[#C3AED3]">
              More Mario Miranda <br /> to puzzle over
            </p>
            <p className="font-content text-left text-[#C3AED3]">
              Classic Mario Miranda art, now as puzzles.
            </p>
            <Link
              className="hover:bg-primary-1-40 disabled:bg-neutral-2-20 text-primary-2-10 disabled:text-neutral-2-40 font-family-content rounded-full bg-[#624796] px-6 py-4 text-base leading-4 font-extrabold tracking-wide uppercase after:content-['_⟶'] disabled:border-0 disabled:opacity-50"
              to={urls.products.jigsawPuzzles(params, {
                artist: 'mario-miranda',
              })}
            >
              SHOP YOUR HEART OUT
            </Link>
          </div>

          {/* Mobile Layout */}
          <div className="flex flex-col items-center gap-y-6 md:hidden">
            <div className="relative flex w-[322px] justify-between overflow-visible">
              <TwPicture
                imgUrl={imgUrl}
                src={'/static/homepage/miranda1.png'}
                alt="Crowded place"
                defaultSize={558}
                twSizes={{ sm: 720, lg: 200 }}
                className="-mt-10 w-auto max-w-[150px] object-contain"
              />
              <TwPicture
                imgUrl={imgUrl}
                src={'/static/homepage/miranda3.png'}
                alt="Mario Miranda"
                defaultSize={558}
                twSizes={{ sm: 720, lg: 200 }}
                className="mt-10 w-auto max-w-[150px] object-contain"
              />
            </div>
            <div className="flex flex-col items-start justify-center gap-y-3 px-4">
              <p className="font-heading-condensed text-4xl leading-[1.1] font-medium tracking-tight text-[#C3AED3]">
                More Mario Miranda <br /> to puzzle over
              </p>
              <p className="font-content text-left text-[#C3AED3]">
                Classic Mario Miranda art, now as puzzles.
              </p>
              <Link
                className="hover:bg-primary-1-40 disabled:bg-neutral-2-20 text-primary-2-10 disabled:text-neutral-2-40 font-family-content rounded-full bg-[#624796] px-6 py-4 text-base leading-4 font-extrabold tracking-wide uppercase after:content-['_⟶'] disabled:border-0 disabled:opacity-50"
                to={urls.products.attributeWithCategory(params, {
                  categorySlug: 'jigsaw-puzzles',
                  attributeSlug: 'puzzle-artist',
                  valueSlug: 'mario-miranda',
                })}
              >
                SHOP YOUR HEART OUT
              </Link>
            </div>
          </div>
        </>
      }
    />,

    <Homepageslides
      key={6}
      varient={'slide6'}
      slideContent={
        <>
          <div className="flex items-center justify-center gap-x-10">
            {['tiger', 'elephant', 'racoon', 'rhino'].map((animal) => (
              <TwPicture
                key={animal}
                imgUrl={imgUrl}
                src={`/static/homepage/${animal}-puzz.png`}
                alt={`${animal} puzzle`}
                defaultSize={558}
                twSizes={{ sm: 720, lg: 200 }}
                className="w-auto max-w-[150px] object-contain md:max-w-[250px]"
              />
            ))}
          </div>
          <div className="flex flex-col items-center justify-center gap-x-8 md:flex-row">
            <p className="font-heading-condensed mt-10 text-center text-5xl leading-12 font-medium text-[#2C1F44] md:mt-0">
              The perfect friends <br /> for little explorers
            </p>
            <div className="flex flex-col">
              <p className="text-[#5F4B83]">
                Meet our 4-in-1, 35 piece puzzles, <br /> Aqua Adventure and Forest Friends <br />
                —full of delightful sea and forest animals!
              </p>
              <div className="flex items-center">
                <Link
                  className="hover:bg-primary-1-40 font-heading disabled:bg-neutral-2-20 disabled:text-neutral-2-40 p-2 text-base font-bold tracking-wide text-[#5F4B83] uppercase after:content-['_⟶'] disabled:border-0 disabled:opacity-50"
                  to={urls.products.attributeWithCategory(params, {
                    categorySlug: 'jigsaw-puzzles',
                    attributeSlug: 'puzzle-series',
                    valueSlug: 'jsn-junior',
                  })}
                >
                  CHECK THEM OUT
                </Link>
                <img src="/static/homepage/logo.svg" alt="logo" />
              </div>
            </div>
          </div>
          <div className="hidden items-center justify-center gap-x-10 md:flex">
            {['whale', 'octopus', 'jellyfish', 'tutrle'].map((animal) => (
              <TwPicture
                key={animal}
                imgUrl={imgUrl}
                src={`/static/homepage/${animal}-puzz.png`}
                alt={`${animal} puzzle`}
                defaultSize={558}
                twSizes={{ sm: 720, lg: 200 }}
                className="w-auto max-w-[250px] object-contain"
              />
            ))}
          </div>
        </>
      }
    />,
  ];

  const mappedProducts = (collections.special || [])
    .map((c) => {
      return {
        name: c.name,
        products: products.collections?.filter((v) => variantHasCollection(v, c)),
      };
    })
    .filter((e) => (e.products || []).length > 0);

  const section_1 = (
    <Section
      image="/static/homepage/section-puzzle-art.png"
      heading="From puzzle to art; preserve your puzzles."
      text="Beyond the challenge, each puzzle piece is a step towards a unique masterpiece. Don't just disassemble your puzzles - frame and display them."
      cta={{
        to: urls.products.categories(params, { slug: 'puzzle-accessories' }),
        text: 'CHECK OUT PUZZLE ACCESSORIES',
      }}
    />
  );
  const section_2 = (
    <Section
      image="/static/homepage/section-custom-puzzle.png"
      heading="Craft your own personal puzzle experience"
      text="Choose an image you love, let that be you and your cat or a beautiful sunset. Send them over and we'll convert them into a customised puzzle, just for you!"
      cta={{ to: urls.customPuzzle.landing(params, {}), text: "LET'S MAKE A CUSTOM PUZZLE" }}
    />
  );

  return (
    <main className="w-full">
      <div className="mt-10">
        <Carousel
          opts={{
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 6000,
            }),
          ]}
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={`slide-${index}`}>{slide}</CarouselItem>
            ))}
          </CarouselContent>
          <CarouselNavigation className="absolute bottom-3" />
        </Carousel>
      </div>

      {products.own && (
        <div className="-mx-6 mt-10 lg:mx-0">
          <ProductCarousel
            heading={
              <>
                Our home grown
                <br />
                JSN Originals
              </>
            }
            variants={products.own}
          />
        </div>
      )}

      {mappedProducts && (
        <div className="mt-10">
          <ProductTabs products={mappedProducts} />
        </div>
      )}

      <div className="-mx-6 mt-10 h-[900px] lg:mx-0">
        <Hero
          heading="Dive into space-themed puzzles"
          text={
            "Calling all space enthusiasts! We've teamed up with A47 from ISRO to create an exciting collection of space puzzles for you to enjoy."
          }
          image={{ src: '/static/homepage/hero-banner-isro.png', width: 1232, height: 900 }}
          cta={{
            to: urls.products.attributeWithCategory(params, {
              categorySlug: 'jigsaw-puzzles',
              attributeSlug: 'puzzle-series',
              valueSlug: 'isro',
            }),
            text: 'Browse collection',
          }}
        />
      </div>

      <div className="mt-10">
        <SectionWithHeading
          heading={
            <>
              Puzzles aren&apos;t just a hobby;
              <br />
              but a form of art.
            </>
          }
        >
          <div className="mt-6 grid grid-rows-2 gap-6 lg:grid-cols-2 lg:grid-rows-1">
            <div className="bg-[#F4DBC5]">{section_1}</div>
            <div className="bg-[#D4E5FE]">{section_2}</div>
          </div>
        </SectionWithHeading>
      </div>

      <div className="bg-primary-1 text-neutral-1 -mx-6 my-10 flex items-center overflow-hidden py-12 text-center lg:mx-0">
        <Carousel
          opts={{
            loop: true,
          }}
          plugins={[
            Autoscroll({
              stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
        >
          <CarouselContent>
            {reviews.map((review, index) => (
              <CarouselItem key={`slide-${index}`} className="basis-1/4 lg:basis-1/5">
                <p className="font-heading-condensed text-xl leading-7 tracking-tight">
                  &ldquo;{review.text}&rdquo;
                </p>
                <p className="">-{review.name}</p>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

    </main>
  );
}
